import type { FC, ReactNode } from 'react'
import type { ProductoResumen } from '@/types'

import Link from 'next/link'
import { Fragment } from 'react'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { cms } from '@/lib/cms'
import { buttonVariants } from '@/components/ui/button'
import AppImage from '@/components/generals/app-image'

interface ProductCardProps {
    producto: ProductoResumen
    tone?: 'graphite' | 'black'
}

export const badgeClass = 'inline-flex items-center rounded-sm border px-2.5 py-1 text-[0.6rem] font-semibold uppercase tracking-[0.2em] backdrop-blur-sm'

const ProductCard: FC<ProductCardProps> = ({ producto, tone = 'graphite' }): ReactNode => {
    const href = `/productos/${producto.slug}`

    return (
        <article
            className={cn(
                'group flex h-full flex-col overflow-hidden rounded-sm border border-ivory/10 transition-colors duration-500 hover:border-brass/45',
                tone === 'graphite' ? 'bg-graphite' : 'bg-black'
            )}
        >
            <div className='relative'>
                <Link href={href} tabIndex={-1} aria-hidden='true' className='relative block aspect-[4/5] overflow-hidden bg-black'>
                    <AppImage
                        src={producto.imagen}
                        alt={producto.imagen_alt}
                        sizes='(min-width: 1024px) 30vw, (min-width: 640px) 45vw, 100vw'
                        className='transition-transform duration-700 ease-out group-hover:scale-[1.04] motion-reduce:transition-none'
                    />
                </Link>

                <div className='pointer-events-none absolute top-4 left-4 flex flex-wrap gap-2'>
                    <span className={cn(badgeClass, 'border-brass/60 bg-black/70 text-brass')}>Personalizable</span>

                    {producto.etiqueta && (
                        <span className={cn(badgeClass, 'border-ivory/20 bg-black/70 text-ivory/85')}>
                            <span data-mdlfy-cms={cms('productos', producto.id, 'etiqueta')}>{producto.etiqueta}</span>
                        </span>
                    )}
                </div>
            </div>

            <div className='flex flex-1 flex-col gap-4 p-6 sm:p-7'>
                {producto.categoria && (
                    <Link
                        href={`/catalogo/${producto.categoria.slug}`}
                        className='w-fit text-[0.65rem] font-semibold uppercase tracking-[0.26em] text-brass transition-colors hover:text-brass-light'
                    >
                        <span data-mdlfy-cms={cms('categorias', producto.categoria.id, 'nombre')}>{producto.categoria.nombre}</span>
                    </Link>
                )}

                <h3 className='font-heading text-[1.65rem] leading-tight font-medium text-foreground'>
                    <Link href={href} className='transition-colors hover:text-brass'>
                        <span data-mdlfy-cms={cms('productos', producto.id, 'nombre')}>{producto.nombre}</span>
                    </Link>
                </h3>

                {producto.descripcion_corta && (
                    <p className='line-clamp-3 text-sm leading-relaxed text-muted-foreground' data-mdlfy-cms={cms('productos', producto.id, 'descripcion_corta')}>
                        {producto.descripcion_corta}
                    </p>
                )}

                {producto.materiales.length > 0 && (
                    <p className='text-xs leading-relaxed text-ivory/70'>
                        <span className='mr-2 font-semibold uppercase tracking-[0.18em] text-steel'>Materiales</span>

                        {producto.materiales.map((material, index) => (
                            <Fragment key={material.id}>
                                {index > 0 && (
                                    <>
                                        <span aria-hidden='true' className='px-1.5 text-brass/70'>·</span>
                                        <span className='sr-only'>, </span>
                                    </>
                                )}
                                <span data-mdlfy-cms={cms('materiales', material.id, 'nombre')}>{material.nombre}</span>
                            </Fragment>
                        ))}
                    </p>
                )}

                <div className='mt-auto pt-3'>
                    <Link href={href} className={buttonVariants({ variant: 'outline', size: 'sm' })}>
                        Consultar producto
                        <span className='sr-only'>: {producto.nombre}</span>
                        <ArrowRight aria-hidden='true' />
                    </Link>
                </div>
            </div>
        </article>
    )
}

export default ProductCard
